import { useLayoutEffect } from 'react';
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import type { UserAuthStatus } from '../types';

/**
 * Redirects users to login page, preserving the authenticated route they were trying to access in
 * the `?redirectAfterLogin` query parameter.
 */
export const useRedirectToLogin = (status: UserAuthStatus) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Layout effect to ensure we grab the route user landed on before React Router has a chance to
  // redirect them. E.g. Going to `/reports` when logged out will land users on `/auth/login`, but
  // layout effect will run before the redirect happens, so we have a chance to grab `/reports` and
  // store it in a search param
  useLayoutEffect(() => {
    // User is logged out, but trying to access a route that's not the login page. Before we bounce
    // them to the login page, we store the route they were trying to access in a search param
    if (status === 'logged-out' && location.pathname !== '/auth/login') {
      const redirectAfterLogin = searchParams.get('redirectAfterLogin');
      let nextRedirectAfterLogin = redirectAfterLogin;

      if (
        // If there's already a ?redirectAfterLogin, don't override it
        !redirectAfterLogin &&
        // If the route they were trying to access is the homepage (`/`), don't bother setting a
        // ?redirectAfterLogin search param
        location.pathname !== '/'
      ) {
        nextRedirectAfterLogin = createSearchParams({
          redirectAfterLogin: location.pathname + location.search,
        }).toString();
      }

      // Redirect user to the login page with the redirect route preserved in the
      // ?redirectAfterLogin search param
      navigate(
        { pathname: '/auth/login', search: nextRedirectAfterLogin ?? '' },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate, searchParams, status]);
};

/**
 * Redirects users to the authenticated route they were trying to access after they've logged in
 */
export const useRedirectAfterLogin = (status: UserAuthStatus) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Layout effect to ensure we grab the route user landed on before React Router has a chance to
  // redirect them. E.g. Going to `/reports` when logged out will land users on `/auth/login`, but
  // layout effect will run before the redirect happens, so we have a chance to grab `/reports` and
  // store it in a search param
  useLayoutEffect(() => {
    // User is logged in, but has a ?redirectAfterLogin query param. This means they were trying to
    // access a route before they were bounced to the login page. Now that they are logged in, we
    // redirect them to the route they were originally trying to access
    if (status === 'logged-in') {
      const redirectAfterLogin = searchParams.get('redirectAfterLogin');
      navigate(redirectAfterLogin ?? '/', { replace: true });
    }
  }, [location.pathname, location.search, navigate, searchParams, status]);
};
